import React, { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  TextInput,
  TextArea,
  Select,
  Badge,
} from '@etherisc/ui-kit';
import { Upload, X, FileText, AlertCircle } from 'lucide-react';
import {
  DocumentTemplateType,
  DocumentTemplateUploadData,
  getTypeLabel,
  formatFileSize,
} from '../../types/documentTemplate';

interface DocumentTemplateUploadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (data: DocumentTemplateUploadData) => Promise<void>;
  loading?: boolean;
}

const TEMPLATE_TYPE_OPTIONS = Object.values(DocumentTemplateType).map(type => ({
  value: type,
  label: getTypeLabel(type),
}));

// Validation schema
const validationSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  type: z.nativeEnum(DocumentTemplateType),
  tags: z.array(z.string()).optional(),
});

type FormData = z.infer<typeof validationSchema>;

export function DocumentTemplateUploadDialog({
  isOpen,
  onClose,
  onUpload,
  loading = false,
}: DocumentTemplateUploadDialogProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [tagInput, setTagInput] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [fileError, setFileError] = useState('');
  
  // React Hook Form setup
  const { register, handleSubmit, formState: { errors }, setValue, watch, reset } = useForm<FormData>({
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
    defaultValues: {
      name: '',
      description: '',
      type: DocumentTemplateType.OTHER,
      tags: [],
    },
  });
  
  const formData = watch();

  const onSubmit = async (data: FormData) => {
    // File validation
    if (!selectedFile) {
      setFileError('File is required');
      return;
    }

    try {
      await onUpload({
        ...data,
        file: selectedFile,
        tags: data.tags || [],
      });
      handleClose();
    } catch (error) {
      setFileError(error instanceof Error ? error.message : 'Upload failed');
    }
  };

  const handleClose = () => {
    reset();
    setSelectedFile(null);
    setTagInput('');
    setFileError('');
    setDragOver(false);
    onClose();
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags?.includes(tag)) {
      setValue('tags', [...(formData.tags || []), tag]);
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setValue('tags', (formData.tags || []).filter(tag => tag !== tagToRemove));
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type based on template type
      if (formData.type === DocumentTemplateType.EMAIL) {
        if (!file.name.endsWith('.html') && !file.name.endsWith('.htm')) {
          setFileError('EMAIL templates must be HTML files (.html or .htm)');
          setSelectedFile(null);
          return;
        }
      } else if (formData.type === DocumentTemplateType.SMS) {
        if (!file.name.endsWith('.txt')) {
          setFileError('SMS templates must be plain text files (.txt)');
          setSelectedFile(null);
          return;
        }
      } else {
        if (!file.name.endsWith('.docx') && !file.name.endsWith('.doc')) {
          setFileError('Document templates must be Word files (.docx or .doc)');
          setSelectedFile(null);
          return;
        }
      }
      
      setSelectedFile(file);
      setFileError('');
      
      // Auto-populate name if not already set
      if (!formData.name) {
        const nameWithoutExtension = file.name.replace(/\.[^/.]+$/, '');
        setValue('name', nameWithoutExtension);
      }
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    const droppedFile = e.dataTransfer.files[0];
    
    // Validate based on template type
    let isValidFile = false;
    if (formData.type === DocumentTemplateType.EMAIL) {
      isValidFile = droppedFile.name.endsWith('.html') || droppedFile.name.endsWith('.htm');
    } else if (formData.type === DocumentTemplateType.SMS) {
      isValidFile = droppedFile.name.endsWith('.txt');
    } else {
      isValidFile = droppedFile.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
         droppedFile.type === 'application/msword' ||
         droppedFile.name.endsWith('.docx') ||
         droppedFile.name.endsWith('.doc');
    }
    
    if (droppedFile && isValidFile) {
      setSelectedFile(droppedFile);
      setFileError('');
      
      // Auto-populate name if not already set
      if (!formData.name) {
        const nameWithoutExtension = droppedFile.name.replace(/\.[^/.]+$/, '');
        setValue('name', nameWithoutExtension);
      }
    } else if (droppedFile) {
      if (formData.type === DocumentTemplateType.EMAIL) {
        setFileError('Please drop an HTML file (.html or .htm)');
      } else if (formData.type === DocumentTemplateType.SMS) {
        setFileError('Please drop a plain text file (.txt)');
      } else {
        setFileError('Please drop a Word document (.docx or .doc)');
      }
    }
  }, [formData.name, formData.type]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="w-full max-w-md mx-4">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Upload Document Template
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={handleClose}
                disabled={loading}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {/* Template Type - First field */}
              <div>
                <label className="block text-sm font-medium mb-1">Template Type *</label>
                <Select
                  value={formData.type}
                  options={TEMPLATE_TYPE_OPTIONS}
                  onValueChange={(value: string) => {
                    setValue('type', value as DocumentTemplateType);
                    // Reset file when type changes
                    setSelectedFile(null);
                    setFileError('');
                  }}
                />
                {formData.type === DocumentTemplateType.SMS && (
                  <p className="text-xs text-blue-600 mt-1">
                    SMS templates must be plain text (.txt) with max 160 characters after variable substitution
                  </p>
                )}
              </div>

              {/* File Upload Area */}
              <div>
                <label className="block text-sm font-medium mb-2">Template File</label>
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                    dragOver
                      ? 'border-blue-400 bg-blue-50'
                      : selectedFile
                      ? 'border-green-400 bg-green-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onClick={() => document.getElementById('file-input')?.click()}
                >
                  <input
                    key={formData.type}
                    id="file-input"
                    type="file"
                    accept={
                      formData.type === DocumentTemplateType.EMAIL 
                        ? ".html,.htm" 
                        : formData.type === DocumentTemplateType.SMS
                        ? ".txt"
                        : ".docx,.doc"
                    }
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  {selectedFile ? (
                    <div className="space-y-2">
                      <FileText className="w-8 h-8 mx-auto text-green-600" />
                      <div>
                        <p className="font-medium text-green-700">{selectedFile.name}</p>
                        <p className="text-sm text-gray-500">{formatFileSize(selectedFile.size)}</p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          setSelectedFile(null);
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="w-8 h-8 mx-auto text-gray-400" />
                      <div>
                        <p className="text-gray-600">
                          {dragOver 
                            ? 'Drop the file here' 
                            : formData.type === DocumentTemplateType.EMAIL
                              ? 'Drag & drop an .html file here'
                              : formData.type === DocumentTemplateType.SMS
                              ? 'Drag & drop a .txt file here'
                              : 'Drag & drop a .docx file here'}
                        </p>
                        <p className="text-sm text-gray-500">
                          or click to browse • {
                            formData.type === DocumentTemplateType.EMAIL 
                              ? 'HTML files only' 
                              : formData.type === DocumentTemplateType.SMS
                              ? 'Plain text files only (.txt)'
                              : 'Word documents only'
                          }
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                {fileError && (
                  <p className="text-sm text-red-600 mt-1 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {fileError}
                  </p>
                )}
              </div>

              {/* Template Name */}
              <div>
                <TextInput
                  label="Template Name *"
                  {...register('name')}
                  placeholder="Enter template name"
                  error={errors.name?.message}
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <TextArea
                  {...register('description')}
                  placeholder="Optional description"
                  rows={3}
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium mb-1">Tags</label>
                <div className="space-y-2">
                  <div className="flex">
                    <TextInput
                      value={tagInput}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTagInput(e.target.value)}
                      onKeyDown={handleTagInputKeyDown}
                      placeholder="Add tag and press Enter"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addTag}
                      className="ml-2"
                    >
                      Add
                    </Button>
                  </div>
                  {(formData.tags?.length || 0) > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {formData.tags?.map(tag => (
                        <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                          {tag}
                          <X
                            className="w-3 h-3 cursor-pointer"
                            onClick={() => removeTag(tag)}
                          />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {fileError && !selectedFile && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {fileError}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-2 pt-4">
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                >
                  {loading ? 'Uploading...' : 'Upload Template'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 